import {
    SETUP_FLOW_MANAGER,
    INIT_FLOW_MANAGER,
    SET_CURRENT_STEP,
    STOP_FORM,
    ADD_STEP_TO_HISTORIC,
    DELETE_LAST_FROM_HISTORIC,
    CLEAR_HISTORIC_UNTIL_STEP,
    CLEAR_ALL_HISTORIC,
} from '@/lib/constants/mutations';

import {
    DEFAULT_VALIDATION_MESSAGE,
} from '@/lib/constants/validations';

import {
    FORM_TYPE,
} from '@/lib/constants/model-types';

const pushToHistoric = (store, step) => {
    store.commit(ADD_STEP_TO_HISTORIC, {
        stepName: step.name,
    });
};

const popFromHistoric = store => store.commit(DELETE_LAST_FROM_HISTORIC);

const clearHistoricUntilStep = (store, step) => {
    store.commit(CLEAR_HISTORIC_UNTIL_STEP, {
        stepName: step.name,
    });
};

const setCurrentStep = async(obj, newStep, navigation) => {
    const {
        logicCallbacks,
        $store,
    } = obj;

    $store.commit(SET_CURRENT_STEP, {
        stepName: newStep.name,
    });

    logicCallbacks.onStepChanged({
        newStep,
        ...navigation,
    });
};

const finishForm = async(flowManager) => {
    const { logicCallbacks, currentStep } = flowManager;

    const resultFinishForm = await logicCallbacks.finishForm(currentStep);

    return resultFinishForm;
};

const emitStepChange = () => {
    window.dispatchEvent(new Event('stepChange'));
};


class FlowManager {
    constructor(initStore, initModelsStore) {
        this.$store = initStore;
        this.$modelsStore = initModelsStore;
    }

    get possibleNextStep() {
        const {
            currentStep,
            currentStepIndex,
            mainForm,
            logicCallbacks,
        } = this;

        return logicCallbacks.decideNextStep({
            currentStep,
            currentStepIndex,
            mainForm,
        });
    }

    get mainForm() {
        return this.$modelsStore.modelForName(
            FORM_TYPE,
            this.$store.state.formData.mainFormName,
        );
    }

    get isAlreadyInitialized() {
        return this.$store.state.formData.initForm;
    }

    get isBlocked() {
        return this.mainForm.transactionalBlockers.length > 0;
    }

    get logicCallbacks() {
        return this.__logicCallbacks;
    }

    get formHistoric() {
        return this.$store.state.formData.formHistoric;
    }

    get isFirstStep() {
        return this.formHistoric.length === 0;
    }

    async init({ stepNameToInit } = {}) {
        if (!this.isAlreadyInitialized) {
            let firstStep = this.mainForm.steps[0];

            if (stepNameToInit) {
                const foundStep = this.mainForm.steps.find(step => step.name === stepNameToInit);
                firstStep = foundStep || firstStep;
            }

            this.$store.commit(INIT_FLOW_MANAGER, {
                currentStep: firstStep,
            });

            await this.logicCallbacks.onInitForm({ firstStep });

            return true;
        }

        return false;
    }

    async setupCurrentForm(mainForm, logicCallbacks) {
        if (this.isAlreadyInitialized) {
            await this.stop();
        }

        this.__logicCallbacks = logicCallbacks;

        this.$store.commit(SETUP_FLOW_MANAGER, {
            mainForm,
        });
    }

    async stop() {
        if (this.isAlreadyInitialized) {
            this.$store.commit(STOP_FORM);
            return true;
        }

        return false;
    }

    get currentStep() {
        if (this.isAlreadyInitialized) {
            return this.mainForm.steps
                .find(step => step.$modelData.name === this.$store.state.formData.currentStepName);
        }

        return null;
    }

    get currentStepName() {
        if (this.currentStep) {
            return this.currentStep.name;
        }

        return '';
    }

    get currentStepIndex() {
        return this.mainForm.stepIds.indexOf(this.currentStep.id);
    }

    get isLastStep() {
        return !this.possibleNextStep;
    }

    async forceValidationsCurrentStep() {
        const {
            currentStep,
        } = this;

        const stepFields = currentStep.fields;
        await Promise.all(stepFields.map(f => f.forceValidations()));
    }

    get isValidCurrentStep() {
        return this.currentStep.isValid;
    }

    async shouldGoToStep(stepToGo) {
        const {
            logicCallbacks,
            currentStep,
        } = this;

        const out = await logicCallbacks.shouldGoToStep({
            possibleStep: stepToGo,
            currentStep,
        });

        if (!out.moveToNextStep) {
            this.forceValidationsCurrentStep();
        }

        return out;
    }

    get previousStep() {
        const { formHistoric, mainForm: { steps } } = this;

        if (formHistoric.length === 0) {
            return false;
        }

        const previousStepName = formHistoric[formHistoric.length - 1];
        const previousStep = steps.find(step => step.name === previousStepName);
        return previousStep;
    }

    async goToStep(stepName) {
        // Only previously visited steps and possibleNextSteps are allowed
        const {
            possibleNextStep,
            formHistoric,
            $store,
        } = this;
        const step = this.mainForm.findByName(stepName);
        const stepIndex = this.mainForm.stepIds.indexOf(step.id);
        const possibleNextStepIndex = this.mainForm.stepIds.indexOf(possibleNextStep.id);

        if (this.isBlocked) {
            return false;
        }

        if (possibleNextStepIndex === stepIndex) {
            const hasGoneNextStep = await this.goToNextStep();

            return hasGoneNextStep.movedStep;
        }

        if (formHistoric.indexOf(stepName) === -1) {
            return false;
        }

        clearHistoricUntilStep($store, step);
        await setCurrentStep(this, step, { isPreviousStep: true });
        return step;
    }

    async goToPreviousStep() {
        if (this.isBlocked) {
            return false;
        }

        const { previousStep, $store } = this;

        if (previousStep) {
            popFromHistoric($store);
            await setCurrentStep(this, previousStep, { isPreviousStep: true });
            emitStepChange();

            return previousStep;
        }

        return false;
    }

    async goToNextStep({ stepToGo = false, skipValidations = false } = {}) {

        if (this.isBlocked) {
            return {
                movedStep: false,
                reasonToStay: 'FORM IS BLOCKED',
            };
        }

        if (!skipValidations) {
            await this.forceValidationsCurrentStep();
        }

        const {
            currentStep,
            $store,
            mainForm,
            possibleNextStep,
        } = this;
        const nextStep = stepToGo ? mainForm.findByName(stepToGo) : possibleNextStep;
        let resultStep;

        if (!skipValidations && !this.isValidCurrentStep) {
            resultStep = { reasonToStay: DEFAULT_VALIDATION_MESSAGE };
        } else {
            resultStep = await this.shouldGoToStep(nextStep);
        }

        if (this.isLastStep && resultStep.moveToNextStep) {
            resultStep = await finishForm(this);

            if (resultStep.finishedForm) {
                return {
                    movedStep: true,
                    finishedForm: true,
                };
            }
        } else {
            if (resultStep.moveToNextStep) {
                pushToHistoric($store, currentStep);

                await setCurrentStep(this, nextStep, { isNextStep: true });

                emitStepChange();

                return {
                    movedStep: true,
                    currentStep: nextStep,
                };
            }

            if (resultStep.onFail) {
                resultStep.onFail(resultStep.feedbackInfo);
            }

            return {
                movedStep: false,
                reasonToStay: resultStep.reasonToStay,
            };
        }

        return {
            movedStep: false,
            reasonToStay: resultStep.reasonToStay,
        };
    }

    setNewFirstStep() {
        this.$store.commit(CLEAR_ALL_HISTORIC);
    }
}

export default FlowManager;